import EditTradeContext from "@shared/features/trades/EditTrade/EditTrade.context";
import { TradeAmountData } from "@shared/features/trades/EditTrade/types";
import { LanguageNameSpaces, OpenMessage, OrderStatus } from "@shared/types";
import { isNumber } from "lodash";
import { useContext } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useEditTradeExtraInfoProps {
  openMessage: OpenMessage;
}

const useEditTradeExtraInfo = ({ openMessage }: useEditTradeExtraInfoProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const {
    setAddAmountModalOpen,
    setAddQuantityModalOpen,
    entryOrders,
    amountData,
    quantityData,
    ordersData,
  } = useContext(EditTradeContext) || {};
  const numActiveEntryOrders = ordersData?.data.number_of_active_entries;
  const checkInvalidAmount = (amountData: TradeAmountData) => {
    let amountValid = true;
    const availableAmount = amountData.getAvailable();
    if (!availableAmount || (amountData.min || 0) > availableAmount) {
      amountValid = false;
    }
    return amountValid;
  };

  const pendingEntriesExist =
    entryOrders &&
    entryOrders.filter((order) => order.status === OrderStatus.PENDING).length >
      0;
  const amountIsValid = amountData && checkInvalidAmount(amountData);
  const quantityIsValid = quantityData && checkInvalidAmount(quantityData);
  const isBotTrade = isNumber(numActiveEntryOrders);

  const onAddAmountClicked = () => {
    if (isBotTrade) {
      openMessage(t("warnings.bot_trade_amount_edit"), "warning");
    } else if (!amountIsValid) {
      openMessage(
        t("warnings.not_enough_coin_to_add", { coin: amountData?.coin }),
        "warning"
      );
    } else if (!pendingEntriesExist) {
      openMessage(t("warnings.no_pending_entry_orders"), "warning");
    } else {
      setAddAmountModalOpen?.(true);
    }
  };

  const onAddQuantityClicked = () => {
    if (!quantityIsValid) {
      openMessage(
        t("warnings.not_enough_coin_to_add", { coin: quantityData?.coin }),
        "warning"
      );
    } else {
      setAddQuantityModalOpen?.(true);
    }
  };

  return [
    {
      key: "addAmount",
      label: t("labels.add_coin_amount", { coin: amountData?.coin || "" }),
      onClick: onAddAmountClicked,
      isInvalid: !amountIsValid || !pendingEntriesExist || isBotTrade,
    },
    {
      key: "addQuantity",
      label: t("labels.add_existing_coin_amount", {
        coin: quantityData?.coin || "",
      }),
      onClick: onAddQuantityClicked,
      isInvalid: !quantityIsValid,
    },
  ];
};

export default useEditTradeExtraInfo;
